import type { Options, Point } from 'highcharts'
import type { ICustomChartOptions } from '~/types/chart'
import type { ISeries } from '~/types/series'

import { SERIES_NAME } from '~/const'
import { analyticsColors } from '~/const/chart'
import { getLegendOptions, getSeriesWithTrend, tooltipUtils } from '~/utils/chart'
import { formatValue } from '~/utils/number'
import { capitalize } from '~/utils/string'

/**
 * It takes a series key, looks it up in the SERIES_NAME const, and returns either the corresponding series name if it exists, or the capitalized series key if it does not
 * @param name A series key, typically in snake_case
 * @param doCapitalize (default = true) If name does not exist in the mapping, should it be capitalized?
 * @return The corresponding series name (or the capitalized series key)
 * @example
 * `formatSingleSeriesName('ads_attr_revenue')` returns 'Ads Attributed Revenue',
 * `formatSingleSeriesName('series_key_with_no_match')` returns 'Series Key With No Match',
 */
const formatSingleSeriesName = (name: string, doCapitalize = true): string =>
  SERIES_NAME[name] ? SERIES_NAME[name] : doCapitalize ? capitalize(name) : name

const formatSeriesName = (series: ISeries[]): ISeries[] =>
  series.map((singleSeries) => ({ ...singleSeries, name: formatSingleSeriesName(singleSeries.name) }))

export const businessReport_lineSerieOptions = (options?: {
  isMoney?: boolean
  period?: number
}): Options & ICustomChartOptions => ({
  seriesFormatter: formatSeriesName,
  yAxis: {
    opposite: false,
    ...(options?.isMoney
      ? {
          title: {
            text: 'euros',
          },
        }
      : {}),
  },
})

export const businessReport_lineWithTrendSerieOptions = (options?: {
  isMoney?: boolean
  period?: number
}): Options & ICustomChartOptions => ({
  seriesFormatter: (series) => {
    // NOTE: the trend is based on the last results, default is 7 so with 4 items there is not enough data to define a trend!
    return getSeriesWithTrend(formatSeriesName(series), options?.period)
  },
  yAxis: [
    {
      opposite: false,
      ...(options?.isMoney
        ? {
            title: {
              text: 'euros',
            },
          }
        : {}),
    },
  ],
})

export const businessReport_columnSerieOptions = (options?: {
  isMoney?: boolean
  period?: number
}): Options & ICustomChartOptions => ({
  chart: {
    type: 'column',
  },
  legend: getLegendOptions('column'),
  seriesFormatter: formatSeriesName,
  yAxis: {
    opposite: false,
    ...(options?.isMoney
      ? {
          title: {
            text: 'euros',
          },
        }
      : {}),
  },
})

export const businessReport_marginMonthlyOptions: Options & ICustomChartOptions = {
  seriesFormatter: (series) => {
    return [
      {
        ...series[0],
        color: analyticsColors[0],
        name: 'Margin',
        type: 'column',
        xAxis: 0,
        yAxis: 1,
      },
      {
        ...series[1],
        color: analyticsColors[1],
        name: 'Margin %',
        tooltip: {
          pointFormatter: function (this: Point): string {
            if (this.y) {
              return `<div><span style="color: ${this.color}">• ${this.series.name}: </span><b>${formatValue(
                this.y * 100
              )}%</b></div>`
            }
            return ''
          },
        },
        type: 'line',
        xAxis: 0,
        yAxis: 0,
      },
    ]
  },
  yAxis: [
    {
      labels: {
        formatter: function (): string {
          return `${(+this.value * 100).toFixed(2)}%`
        },
        style: {
          color: analyticsColors[1],
        },
      },
      opposite: true,
      title: {
        style: {
          color: analyticsColors[1],
        },
      },
    },
    {
      labels: {
        style: {
          color: analyticsColors[0],
        },
      },
      opposite: false,
      title: {
        style: {
          color: analyticsColors[0],
        },
        text: 'euros',
      },
    },
  ],
}

export const businessReport_stackedColumnOptions = (options?: {
  isMoney?: boolean
}): Options & ICustomChartOptions => ({
  chart: {
    type: 'column',
  },
  legend: getLegendOptions('column'),
  seriesFormatter: (series: ISeries[]) => {
    return series.map((singleSeries) => ({
      ...singleSeries,
      name: formatSingleSeriesName(singleSeries.name),
      stacking: 'normal',
    }))
  },
  yAxis: {
    opposite: false,
    ...(options?.isMoney
      ? {
          title: {
            text: 'euros',
          },
        }
      : {}),
  },
})

export const businessReport_spendAndRevenueMonthlyColumnOptions: Options & ICustomChartOptions = {
  chart: {
    type: 'column',
  },
  legend: getLegendOptions('column'),
  seriesFormatter: (series: ISeries[]) => {
    return series.map((singleSeries, index: number) => {
      return {
        ...singleSeries,
        name: formatSingleSeriesName(singleSeries.name),
        stack: index === 0 ? 0 : 1,
        stacking: 'normal',
      }
    })
  },
  tooltip: tooltipUtils.getStackingToolTip(),
  yAxis: [
    {
      opposite: false,
      title: {
        text: 'euros',
      },
    },
  ],
}

// Refunds Units & Revenue
export const businessReport_refundsUnitAndRevenueOptions: Options & ICustomChartOptions = {
  seriesFormatter: (series) => {
    return [
      {
        ...series[0],
        name: formatSingleSeriesName(series[0].name),
        type: 'column',
        xAxis: 0,
        yAxis: 1,
      },
      {
        ...series[1],
        name: formatSingleSeriesName(series[1].name),
        type: 'line',
        xAxis: 0,
        yAxis: 0,
      },
    ]
  },
  yAxis: [
    {
      labels: {
        style: {
          color: analyticsColors[1],
        },
      },
      opposite: false,
      title: {
        style: {
          color: analyticsColors[1],
        },
        text: 'euros',
      },
    },
    {
      labels: {
        style: {
          color: analyticsColors[0],
        },
      },
      opposite: true,
      title: {
        style: {
          color: analyticsColors[0],
        },
      },
    },
  ],
}
